import React, { useContext, useEffect, useState } from "react";
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  UncontrolledTooltip,
  Button,
  Spinner,
} from "reactstrap";
import styles from "../../assets/scss/common/common.module.scss";
import { useNavigate, useLocation } from "react-router-dom";
import { LIBRARY_PATHS, MASTER_STUDY_PATHS, DASHBOARD_PATHS, ACCESSIONING_PATHS } from "Routes/paths";
import CommonForm, { FormFieldConfig } from "Components/Common/CommonForm";
import { useForm } from "react-hook-form";
import { useMasterStudyService } from "modules/Clinical/StudyDetails/api/useStudyQuery";
import { useGlobalStudy } from "../../context/StudyContext";
import BreadCrumb from "Components/Common/BreadCrumb";
import { BreadcrumbContext } from "../../Layouts/Header";
import { getFirstAccessiblePath, GetUserData, showToastMessage } from "common/service/CommonService";
import TruncatedText from "./TruncatedText";
import { usePermission } from "context/PermissionContext";
import { PERMISSION_MODULES, PERMISSION_ACTIONS } from "common/constants/PermissionConstants";
import { useVerificationService } from "modules/Verification/api/useVerificationQuery";
import VerificationQueue from "modules/Verification/pages/VerificationQueue";
import FullPageLoader from "./FullPageLoader";

/* ----------------------------------
* Types
* ---------------------------------- */

export interface CommonTabHeaderProps {
  title: string;
  isOpen?: boolean;
  editableTitle?: string; // 👈 workflow name
  onTitleChange?: (value: string) => void; // 👈 setter
  validationRule?: (value: string) => true | string; // validation function reference
  toggle?: () => void;
  showActionHandler?: () => void;
  activeTab?: string;
  showAction?: boolean;
  // Whether to render the action (Save) button inside the header.
  // Some pages (like AddTemplate) prefer to render the save button in-page.
  showActionInHeader?: boolean;
  exitPath?: string;
  onExit?: () => void;
}

/* ----------------------------------
* Helper
* ---------------------------------- */

/* Maps page title keywords to remix-icon class names */
const getTitleIcon = (title: string | null): string => {
  if (!title) return "ri-layout-grid-line";
  const t = title.toLowerCase();
  if (t.includes("dashboard")) return "ri-dashboard-line";
  if (t.includes("volunteer")) return "ri-user-heart-line";
  if (t.includes("study")) return "ri-microscope-line";
  if (t.includes("crf")) return "ri-file-list-3-line";
  if (t.includes("template")) return "ri-layout-2-line";
  if (t.includes("codelist")) return "ri-code-s-slash-line";
  if (t.includes("checklist")) return "ri-checkbox-multiple-line";
  if (t.includes("workflow")) return "ri-git-branch-line";
  if (t.includes("role")) return "ri-shield-user-line";
  if (t.includes("user")) return "ri-team-line";
  if (t.includes("design")) return "ri-palette-line";
  if (t.includes("adverse")) return "ri-alert-line";
  if (t.includes("ip") || t.includes("inventory")) return "ri-flask-line";
  if (t.includes("dispensing")) return "ri-medicine-bottle-line";
  if (t.includes("aliquot")) return "ri-test-tube-line";
  if (t.includes("sponsor")) return "ri-building-2-line";
  if (t.includes("period")) return "ri-time-line";
  if (t.includes("transfer")) return "ri-exchange-line";
  if (t.includes("disposal") || t.includes("destruction")) return "ri-delete-bin-line";
  if (t.includes("retention")) return "ri-archive-line";
  if (t.includes("print")) return "ri-printer-line";
  if (t.includes("scan")) return "ri-qr-scan-2-line";
  if (t.includes("accession")) return "ri-barcode-line";
  if (t.includes("reconcil")) return "ri-equalizer-line";
  if (t.includes("report")) return "ri-bar-chart-2-line";
  if (t.includes("audit")) return "ri-shield-check-line";
  if (t.includes("training")) return "ri-graduation-cap-line";
  if (t.includes("company")) return "ri-building-line";
  if (t.includes("setting")) return "ri-settings-3-line";
  if (t.includes("password")) return "ri-lock-line";
  if (t.includes("session")) return "ri-time-line";
  if (t.includes("document")) return "ri-folder-open-line";
  if (t.includes("returns")) return "ri-arrow-go-back-line";
  if (t.includes("stock")) return "ri-stock-line";
  return "ri-pages-line";
};

/* ----------------------------------
* Component
* ---------------------------------- */

const CommonTabHeader: React.FC<CommonTabHeaderProps> = ({
  title,
  isOpen,
  toggle,
  activeTab,
  showAction,
  showActionHandler,
  editableTitle,
  onTitleChange,
  validationRule,
  exitPath,
  onExit,
  showActionInHeader = true,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempTitle, setTempTitle] = useState(editableTitle || "");
  const [isVQDrawerOpen, setIsVQDrawerOpen] = useState<boolean>(false);
  const navigate = useNavigate();

  // Breadcrumb context (pages may set breadcrumbs via provider)
  const breadcrumbContext = useContext(BreadcrumbContext);
  const breadcrumbItems = breadcrumbContext?.breadcrumbItems ?? [];

  // Study select (reuse Header approach)
  const { hasPermission } = usePermission();
  const { isModuleVisible } = usePermission();
  const location = useLocation();
  const { DropDownAllQuery: studySelectAllQuery } = useMasterStudyService({
    includedraft: location.pathname === MASTER_STUDY_PATHS.LIST,
    enabledDropDown: true,
  });
  const user = GetUserData()

  const hasAllStudiesRight = hasPermission(PERMISSION_MODULES.GLOBAL_STUDIES, PERMISSION_ACTIONS.ALL_STUDIES);

  const studyOptions: Array<{
    label: string | number;
    value: string | number;
  }> = hasAllStudiesRight
      ? (((studySelectAllQuery.data as any)?.data || []).map((study: any) => ({
        label: study.studycode,
        value: study.studyid,
      })))
      : ((user?.studies as any[])?.map((study: any) => ({
        label: study.studycode,
        value: study.studyid,
      })) || []);

  const hasUserStudies = Array.isArray(user?.studies) && user.studies.length > 0;

  const { selectedStudy: globalStudy, setGlobalStudy } = useGlobalStudy();

  // Notification count
  const { NotificationCountQuery } = useVerificationService({
    studyid: globalStudy?.studyid ? Number(globalStudy.studyid) : null,
  });
  const notificationCount: number = NotificationCountQuery?.data?.data;

  const { control, setValue } = useForm<{ selectedStudy: string | number | null }>({
    defaultValues: { selectedStudy: null },
  });
  useEffect(() => {
    const globalStudyInOptions = globalStudy && studyOptions.some(
      (s: any) => String(s.value) === String(globalStudy.studyid)
    );

    if (user?.studyid) {
      if (!globalStudy || String(globalStudy.studyid) !== String(user.studyid)) {
        setGlobalStudy({ studyid: String(user.studyid), studyname: String(user.studycode) });
      }
    } else if (!globalStudy || !globalStudyInOptions) {
      if (studyOptions.length > 0) {
        const firstStudy = studyOptions[0];
        setGlobalStudy({ studyid: String(firstStudy.value), studyname: String(firstStudy.label) });
      }
    }

    if (globalStudy) {
      const foundOption = studyOptions.find(
        (opt: any) => String(opt.value) === String(globalStudy.studyid)
      );
      setValue("selectedStudy", foundOption ? foundOption.value : globalStudy.studyid);
    } else {
      setValue("selectedStudy", null);
    }
  }, [globalStudy, studyOptions, setValue, user?.studyid, user?.studycode, setGlobalStudy]);

  const studyFormFields: FormFieldConfig[] = [
    {
      name: "selectedStudy",
      type: "select",
      placeholder: "Select Study",
      options: studyOptions,
      disabled: !!user?.studyid || studySelectAllQuery.isLoading,
      isClearable: false,
      onChangeExtra(selected) {
        if (selected) {
          setGlobalStudy({ studyid: String(selected.value), studyname: String(selected.label) });
        } else {
          setGlobalStudy(null);
        }
        const firstAccessiblePath = getFirstAccessiblePath(undefined, undefined, { isModuleVisible, hasPermission });
        navigate(firstAccessiblePath);
      },
    },
  ];

  useEffect(() => {
    setTempTitle(editableTitle || "");
  }, [editableTitle]);

  const toDomId = (value: string) =>
    (value ?? "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9_-]/g, "-");

  const tooltipId = `${toDomId(title)}-action-menu`;

  const navigateToWorkflowList = () => {
    navigate(LIBRARY_PATHS.WORKFLOW_LIST);
  };
  
  //   if(studySelectAllQuery.isLoading){
  //   return <FullPageLoader />;
  // }

  return (
    <>
      <div
        className={`d-flex justify-content-between align-items-center ${styles.heading_container} mb-0`}
      >
        {/* ── Left: title + icon + breadcrumbs ── */}
        <div className="d-flex align-items-center gap-2">
          {!editableTitle && (
            <div className="header-page-title align-items-center">
              {/* Icon badge */}
              <div className="page-title-icon">
                <i className={getTitleIcon(title)} />
              </div>
              {/* Text */}
              <div className="page-title-text">
                <h5 className="mb-sm-0 text-black fw-semibold d-flex align-items-center">
                  <TruncatedText text={title} maxWidth="400px" />
                </h5>
              </div>
            </div>
          )}

          {editableTitle && (
            <div className="header-page-title align-items-center">
              {/* Icon badge */}
              <div className="page-title-icon">
                <i className={getTitleIcon(editableTitle)} />
              </div>
              {/* Text */}
              <div className="page-title-text d-flex align-items-center">
                {!isEditing ? (
                  <span
                    className="fw-semibold text-primary cursor-pointer d-inline-flex align-items-center"
                    onClick={() => setIsEditing(true)}
                    title="Click to edit workflow name"
                    style={{ fontSize: "14px" }}
                  >
                    <TruncatedText text={editableTitle} maxWidth="400px" />
                    <i className="ri-pencil-line ms-1 fs-14" />
                  </span>
                ) : (
                  <input
                    autoFocus
                    className="form-control form-control-sm"
                    style={{ width: 220 }}
                    value={tempTitle}
                    onChange={(e) => setTempTitle(e.target.value)}
                    onBlur={() => {
                      if (validationRule) {
                        const validationResult = validationRule(tempTitle);
                        if (validationResult !== true) {
                          showToastMessage("error", validationResult);
                          setTempTitle(editableTitle || "");
                          setIsEditing(false);
                          return;
                        }
                      }
                      if (tempTitle.trim()) {
                        onTitleChange?.(tempTitle.trim());
                      }
                      setIsEditing(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        if (validationRule) {
                          const validationResult = validationRule(tempTitle);
                          if (validationResult !== true) {
                            showToastMessage("error", validationResult);
                            setTempTitle(editableTitle || "");
                            setIsEditing(false);
                            return;
                          }
                        }
                        if (tempTitle.trim()) {
                          onTitleChange?.(tempTitle.trim());
                        }
                        setIsEditing(false);
                      }
                      if (e.key === "Escape") {
                        setTempTitle(editableTitle);
                        setIsEditing(false);
                      }
                    }}
                  />
                )}
              </div>
            </div>
          )}

          {/* Breadcrumbs – shown inline next to title when present */}
          {breadcrumbItems.length > 0 && (
            <div className="d-none d-md-flex align-items-center ms-2">
              <span className="header-divider"></span>
              <BreadCrumb items={breadcrumbItems} />
            </div>
          )}
        </div>

        {/* ── Right: study dropdown + action buttons ── */}
        <div className="header-right-section align-items-center">
          {/* Study selector */}
          <div className="study-dropdown-container" style={{ minWidth: 220 }}>
            <CommonForm
              fields={studyFormFields}
              control={control}
              columns={1}
              parentClass="m-0"
              labelShow={false}
              childClass="mb-0"
            />
          </div>

          {/* Notification Bell */}
          <button
            type="button"
            className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle header-notification-btn position-relative"
            title="Notifications"
            id="tab-header-notification-bell-btn"
            onClick={() => setIsVQDrawerOpen(true)}
            style={{ top: '4px' }}
          >
            <i className="ri-notification-3-line fs-22"></i>
            <span className="position-absolute topbar-badge fs-10 translate-middle badge rounded-pill bg-danger">
              {NotificationCountQuery.isFetching ? (
                <span
                  style={{
                    display: 'inline-block',
                    width: '6px',
                    height: '6px',
                    borderRadius: '4px',
                    background: 'rgba(255,255,255,0.5)',
                    animation: 'pulse 1s ease-in-out infinite',
                  }}
                />
              ) : (
                notificationCount
              )}
            </span>
          </button>

          {showAction && showActionInHeader && (
            <>
              <span className="header-divider"></span>
              <div className="d-flex gap-2">
                <Button
                  size="sm"
                  color="primary"
                  className="px-3"
                  disabled={isOpen}
                  onClick={showActionHandler}
                >
                  {isOpen && <Spinner size="sm" className="me-2" />}
                  {activeTab ? activeTab : "Save Workflow"}
                </Button>
                {activeTab ? null : (
                  <Button color="secondary" size="sm" outline onClick={navigateToWorkflowList}>
                    Cancel
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      <VerificationQueue
        isOpen={isVQDrawerOpen}
        toggle={() => setIsVQDrawerOpen(false)}
      />
    </>
  );
};

export default CommonTabHeader;
 